// DTOs for the AtsExplorer endpoints on GW.Services.AtsIntegrationService

export type AtsPaginationKind = "Page" | "Scroll" | "Offset" | "None"

export type AtsFilterDataType = "reference" | "boolean" | "number" | "string" | "datetime"

export interface AtsFilterDefinition {
  /** The filter field name to send in AtsExplorerFilter.field */
  name: string
  displayName: string
  dataType: AtsFilterDataType
  /** When dataType is "reference", the reference data set supplying valid values */
  referenceType: string | null
  /** Multiple values allowed — send one AtsExplorerFilter entry per value */
  allowsMultiple: boolean
  /** A value must be supplied before records can be listed (e.g., parent ID of a nested resource) */
  isRequired: boolean
  /**
   * Operators the vendor accepts ("eq" only for Loxo; Bullhorn adds comparisons, null
   * checks, to-many membership and substring matching). Value-less operators (isNull,
   * isNotNull, isEmpty, isNotEmpty) are sent with an empty value.
   */
  supportedOperators: string[]
  /** Targets a tenant-defined custom field — folded behind a toggle in pickers */
  isCustom: boolean
}

export interface AtsReferenceDataItem {
  id: string
  name: string
}

export interface AtsEntityTypeInfo {
  name: string
  displayName: string
  /** Browsing group for large catalogs (Bullhorn exposes 100+ entities); null when ungrouped */
  group: string | null
  supportsKeywordSearch: boolean
  supportsFieldSelection: boolean
  supportsFilters: boolean
  /**
   * The entity-types listing carries the curated filters only; the per-entity endpoint
   * (useEntityTypeDetail) returns the complete set including metadata-derived filters
   */
  filters: AtsFilterDefinition[]
  /** False for nested resources with no standalone detail endpoint */
  supportsDetailFetch: boolean
  paginationKind: AtsPaginationKind
  /** Curated paths preselected in field pickers when supportsFieldSelection is true */
  defaultFields: string[]
  /** Paths the ATS can order a listing by; absent or empty when the vendor cannot sort */
  sortableFields?: string[] | null
}

export interface AtsExplorerFilter {
  field: string
  operator: string
  value: string
}

/** Server-side ordering of a listing; the field must be one of the entity's sortableFields */
export interface AtsListSort {
  field: string
  descending: boolean
}

export interface AtsListRequest {
  entityType: string
  searchText?: string | null
  filters?: AtsExplorerFilter[] | null
  fields?: string[] | null
  sort?: AtsListSort | null
  pageSize?: number
  cursor?: string | null
}

export interface AtsRecordPage {
  records: Record<string, unknown>[]
  nextCursor: string | null
  totalCount: number | null
}

export type AtsFieldKind = "Scalar" | "Id" | "Composite" | "ToOne" | "ToMany"

export interface AtsFieldDescriptor {
  path: string
  dataType: string
  isArray: boolean
  sampleValue: string | null
  /** Vendor display label when metadata declares one */
  label?: string | null
  /** Structural role per vendor metadata; "Scalar" for sample-inferred schemas */
  kind?: AtsFieldKind
  /**
   * For an association node: the entity type whose schema supplies the nested fields.
   * Expand by fetching that entity's schema and prefixing its paths with `path + "."`.
   */
  associatedEntityType?: string | null
  isExpandable?: boolean
  /** Tenant-defined custom field (Bullhorn "custom…" and "correlatedCustom…" names) */
  isCustom?: boolean
}

export interface AtsSchemaRequest {
  sampleSize: number
  filters?: AtsExplorerFilter[] | null
  /** Association nodes to expand server-side ("jobOrder", "candidate.owner") */
  expandPaths?: string[] | null
}

export interface AtsEntitySchema {
  entityType: string
  source: "Meta" | "SampleInference"
  sampleSize: number
  fields: AtsFieldDescriptor[]
}

/** Server output formats; "Xlsx" is a client-side conversion of a CSV pull, or of a JSON pull when a field gets its own sheet */
export type AtsPullFormat = "Csv" | "Json" | "Xlsx"

/** Raw vendor values, or friendly ones (ISO dates, HTML stripped, to-many items formatted) */
export type AtsPullValueFormat = "Raw" | "Friendly"

/**
 * How the server lays out one selected to-many association: List joins the items into
 * one cell per sub-field, Rows repeats the parent once per item (one field per pull),
 * Nested leaves the items in place (JSON only). CSV defaults to List, JSON to Nested.
 */
export type AtsToManyMode = "List" | "Rows" | "Nested"

export interface AtsPullToManyOption {
  /** The association's root path, prefixed with its entity key in a composite pull ("Placement.commissions") */
  field: string
  mode: AtsToManyMode
}

/**
 * A related entity included in a multi-entity raw pull. The related record's ID is read
 * from each primary record at referencePath (ReferenceId notation: dot paths, "[]" for
 * first element, JSONPath filter expressions).
 */
export interface AtsPullRelatedEntity {
  /** Key for this entity's payload in the composite output record */
  name: string
  /** Explorer catalog entity type (must support detail fetch) */
  entityType: string
  /** Path into the primary record resolving to the related record's ID */
  referencePath: string
}

export interface AtsPullRequest {
  entityType: string
  searchText?: string | null
  filters?: AtsExplorerFilter[] | null
  fields?: string[] | null
  maxRecords: number
  /** Server formats only ("Csv" | "Json"); the wizard converts "Xlsx" client-side from a CSV pull, or from a JSON pull when a field gets its own sheet */
  format: Exclude<AtsPullFormat, "Xlsx">
  /** Defaults server-side to Friendly for CSV and Raw for JSON */
  valueFormat?: AtsPullValueFormat | null
  /**
   * When present, output records are composites keyed by primary entity type + each name,
   * and every path in `fields` must be prefixed with its entity key ("Placement.status")
   */
  relatedEntities?: AtsPullRelatedEntity[] | null
  /** Layout per selected to-many association; the format's default applies to any left out */
  toMany?: AtsPullToManyOption[] | null
}

// Mirrors AtsEntityMapping in the integration service (used by mapped-pull)
export interface AtsExplorerEntityMapping {
  entityType: string
  name: string
  id?: string | null
  referenceId?: string | null
  fieldMappings: {
    sourceField: string
    destinationField: string
    isRequired?: boolean
    dataTypeConverter?: string | null
    valueMappings?: { sourceValue: unknown; destinationValue: unknown }[] | null
    isValueMappingRequired?: boolean
    defaultValueMapping?: unknown
  }[]
}

export interface AtsMappedPullRequest {
  entityType: string
  searchText?: string | null
  filters?: AtsExplorerFilter[] | null
  maxRecords: number
  entities: AtsExplorerEntityMapping[]
  includeRelatedEntities: boolean
}

export interface AtsMappedPullResult {
  rows: Record<string, unknown>[]
  /** Pulled records that produced no row (missing id or empty mapping result) */
  skippedCount: number
}

export const PULL_MAX_RECORDS = 10_000
export const PULL_MAX_RECORDS_COMPOSITE = 1_000
